import React, { FC } from "react";
import {
  alpha,
  Box,
  rgbToHex,
  SliderPropsColorOverrides,
  useTheme,
} from "@mui/material";
import { Ranger } from "@tanstack/react-ranger";
import { OverridableStringUnion } from "@mui/types";
import { DateTime } from "luxon";

export interface SliderHandleProps {
  ranger: Ranger<HTMLDivElement>;
  value: number;
  valueFormatter: (value: number) => string;
  isActive: boolean;
  onKeyDownHandler: (args: any) => void;
  onMouseDownHandler: (args: any) => void;
  onTouchStart: (args: any) => void;

  /**
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#custom-colors).
   * @default 'primary'
   */
  color?: OverridableStringUnion<
    "primary" | "secondary" | "error" | "info" | "success" | "warning",
    SliderPropsColorOverrides
  >;
}

export const SliderHandle: FC<SliderHandleProps> = (props) => {
  const theme = useTheme();
  const color = props.color ?? theme.palette.primary.main;
  const valueLabel = props.valueFormatter(props.value);
  return (
    <Box
      component="button"
      onKeyDown={props.onKeyDownHandler}
      onMouseDown={props.onMouseDownHandler}
      onTouchStart={props.onTouchStart}
      role="slider"
      aria-valuemin={props.ranger.options.min}
      aria-valuemax={props.ranger.options.max}
      aria-valuenow={props.value}
      sx={{
        position: "absolute",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "14px",
        height: "14px",
        outline: "none",
        borderRadius: "100%",
        background: `linear-gradient(to bottom, ${color} 45%, ${color} 55%)`,
        border: "solid 1px #888",
        cursor: "ew-resize",
      }}
      style={{
        left: `${props.ranger.getPercentageForValue(props.value)}%`,
        zIndex: props.isActive ? "1" : "0",
      }}
    >
      <Box
        component={"span"}
        sx={{ position: "absolute", transform: "translateY(-150%)" }}
      >
        {valueLabel}
      </Box>
    </Box>
  );
};
